import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Palette, Ruler, MessageCircle, HeartHandshake } from 'lucide-react';
import styled from 'styled-components';

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  background-color: ${props => props.theme.colors.background.main};
  position: relative;
  overflow: hidden;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 8rem 0;
  }
`;

const DecorativeBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom right, ${props => props.theme.colors.primary.main}08, transparent, ${props => props.theme.colors.accent.main}08);
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
  position: relative;
  z-index: 10;
`;

const ContentWrapper = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  text-align: center;
`;

const SectionHeader = styled.div`
  margin-bottom: 3rem;
`;

const Subtitle = styled.span`
  color: ${props => props.theme.colors.accent.gold};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const Title = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['5xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['6xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['7xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => props.theme.typography.fontSize['8xl']};
  }
`;

const Description = styled.p`
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.fontSize.lg};
  line-height: 1.7;
  margin-bottom: 3rem;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.xl};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
  }
`;

const PromiseCard = styled.div`
  background-color: ${props => props.theme.colors.background.card};
  border: 1px solid ${props => props.theme.colors.border.main};
  border-radius: ${props => props.theme.borderRadius['3xl']};
  padding: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 3rem;
  }
`;

const PromiseTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const PromisesGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PromiseItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.lg};
  text-align: left;
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.xl};
  background-color: ${props => props.theme.colors.background.main};
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    background-color: ${props => props.theme.colors.primary.main}0D;
  }
`;

const PromiseIcon = styled.div`
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary.main}1A;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${props => props.theme.colors.primary.main};
  }
`;

const PromiseText = styled.p`
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.7;
  margin: 0;
`;

const AboutSection = () => {
  const { t } = useLanguage();

  const promises = [
    { icon: Palette, text: t('about.item1') },
    { icon: Ruler, text: t('about.item2') },
    { icon: MessageCircle, text: t('about.item3') },
    { icon: HeartHandshake, text: t('about.item4') },
  ];

  return (
    <Section>
      <DecorativeBackground />
      <Container>
        <ContentWrapper>
          <SectionHeader>
            <Subtitle>{t('about.subtitle')}</Subtitle>
            <Title>{t('about.title')}</Title>
            <Description>{t('about.description')}</Description>
          </SectionHeader>

          <PromiseCard>
            <PromiseTitle>{t('about.promise')}</PromiseTitle>
            <PromisesGrid>
              {promises.map((promise, index) => (
                <PromiseItem key={index}>
                  <PromiseIcon>
                    <promise.icon />
                  </PromiseIcon>
                  <PromiseText>{promise.text}</PromiseText>
                </PromiseItem>
              ))}
            </PromisesGrid>
          </PromiseCard>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default AboutSection;
